/**
 * Better Auth type definitions
 * Extends the default Better Auth types with our custom fields
 */

import { z } from 'zod';
import { FEATURES } from '@/lib/subscriptions-client';

/**
 * Re-export unified User type for auth compatibility
 */
export type { User as AuthUser } from '@/types/user';

import type { User } from '@/types/user';

export interface AuthSession {
  session: {
    id: string;
    userId: string;
    expiresAt: Date;
    createdAt: Date;
    updatedAt: Date;
    token: string;
    ipAddress?: string | null;
    userAgent?: string | null;
  };
  user: User;
}

// Helper functions for type guards
export function isAdmin(user: { role?: string | null | undefined } | null | undefined): boolean {
  return user?.role === 'admin';
}

export function isPro(user: User | null | undefined): boolean {
  return user?.role === 'pro' || user?.role === 'admin';
}

// Get features for a session
export function getFeaturesForSession(session: AuthSession | null): string[] {
  if (!session?.user) {
    return [];
  }
  return getFeaturesForRole(session.user.role);
}

// Get features based on role
export function getFeaturesForRole(role: 'free' | 'pro' | 'admin' | undefined): string[] {
  if (role === 'admin') {
    // Admins get all features
    return Object.values(FEATURES);
  }

  if (role === 'pro') {
    // Pro users get most features
    return [
      FEATURES.TIMELINE,
      FEATURES.TIMELINE_INSIGHTS,
      FEATURES.SOCIAL_CIRCLE,
      FEATURES.SOCIAL_ANALYTICS,
      FEATURES.PROFILE_ANALYSIS,
      FEATURES.ARCHIVE_IMPORT,
      FEATURES.TWEET_SEARCH,
      FEATURES.DIGEST_DAILY,
      FEATURES.USER_ANALYSIS_DATE_RANGE,
      FEATURES.AI_MODEL_SELECTION,
    ];
  }

  // Free users get basic features
  return [FEATURES.TIMELINE, FEATURES.TIMELINE_INSIGHTS, FEATURES.SOCIAL_CIRCLE, FEATURES.TWEET_SEARCH];
}

export function isAuthSession(value: unknown): value is AuthSession {
  return authSessionSchema.safeParse(value).success;
}

const authUserSchema = z
  .object({
    id: z.string(),
    username: z.string(),
    createdAt: z.instanceof(Date),
    updatedAt: z.instanceof(Date),
    lastFetchedAt: z.instanceof(Date),
  })
  .passthrough();

const authSessionSchema = z
  .object({
    user: authUserSchema,
    session: z
      .object({
        id: z.string(),
        userId: z.string(),
        expiresAt: z.instanceof(Date),
        createdAt: z.instanceof(Date),
        updatedAt: z.instanceof(Date),
        token: z.string(),
        ipAddress: z.string().nullable().optional(),
        userAgent: z.string().nullable().optional(),
      })
      .passthrough(),
  })
  .passthrough();
